<template>
  <div
    ref="modal"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="staticBackdropLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <div class="head-stack">
            <span class="head-label" id="staticBackdropLabel">
              {{ page?.id ? "Edit page" : "Add page" }}
            </span>
            <input
              type="text"
              class="name-input"
              v-model="name"
              placeholder="Page name"
              title="Click to rename the page"
            />
          </div>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            v-on:click="$emit('update:show', false)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="24"
              height="24"
            >
              <path fill="none" d="M0 0h24v24H0z" />
              <path
                d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z"
              />
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <template v-if="page_type != 1">
            <div class="lang-section">
              <div class="lang-head">
                <span class="field-label">SEO title & description</span>
                <span
                  class="badge-soft"
                  :class="
                    completedLanguages === languages.length
                      ? 'badge-green'
                      : 'badge-yellow'
                  "
                  v-if="languages.length > 0"
                >
                  {{ completedLanguages }}/{{ languages.length }} languages done
                </span>
              </div>
              <nav class="pill-nav lang-pills" v-if="languages.length > 0">
                <button
                  v-for="lang in languages"
                  :key="lang.code"
                  :class="{ active: selectedLanguage?.code === lang.code }"
                  :title="langStatusTitle(lang)"
                  v-on:click="selectedLanguage = lang"
                >
                  <span
                    class="lang-dot"
                    :class="'lang-' + langStatus(lang.code)"
                  ></span>
                  {{ lang.code.toUpperCase() }}
                </button>
              </nav>
              <template v-if="selectedLanguage">
                <label class="field mb-2">
                  <div class="field-head">
                    <span class="field-label"
                      >{{ selectedLanguage.name }} title</span
                    >
                    <span
                      class="count-hint"
                      :class="'count-' + titleState.tone"
                    >
                      {{ titleState.text }}
                    </span>
                  </div>
                  <input
                    type="text"
                    :class="{ 'input-empty': titleState.tone === 'empty' }"
                    v-model="title[selectedLanguage.code]"
                    :placeholder="fallbackFor('title')"
                  />
                </label>
                <label class="field">
                  <div class="field-head">
                    <span class="field-label"
                      >{{ selectedLanguage.name }} description</span
                    >
                    <span
                      class="count-hint"
                      :class="'count-' + descriptionState.tone"
                    >
                      {{ descriptionState.text }}
                    </span>
                  </div>
                  <input
                    type="text"
                    :class="{
                      'input-empty': descriptionState.tone === 'empty',
                    }"
                    v-model="description[selectedLanguage.code]"
                    :placeholder="fallbackFor('description')"
                  />
                </label>
                <!-- Say what is wrong with the language being edited right
                     here; the pills only carry a colour, and "which field"
                     was the part nobody could see. -->
                <div class="issue-line mt-2" v-if="selectedIssues.length > 0">
                  <span class="dot dot-yellow"></span>
                  <span>{{ selectedIssues.join(" · ") }}</span>
                </div>
                <div class="hint-text mt-1" v-if="missingLanguageNames">
                  Still to do: {{ missingLanguageNames }}
                </div>
              </template>
            </div>

            <label class="field mb-3">
              <span class="field-label">Page address</span>
              <div class="slug-input">
                <span class="slug-domain">{{ slugDomain }}/</span>
                <input type="text" v-model="slug" placeholder="page-name" />
              </div>
              <span class="hint-text mt-1">
                This is the web address people will open. Use lowercase words
                separated by hyphens.
              </span>
            </label>

            <label class="field mb-3">
              <span class="field-label">Category</span>
              <input
                type="text"
                v-model="categoryValue"
                list="page-category-options"
                placeholder="e.g. Campaign, Legal, Info…"
              />
              <datalist id="page-category-options">
                <option
                  v-for="option in categorySuggestions"
                  :key="option"
                  :value="option"
                />
              </datalist>
            </label>
          </template>

          <label class="field mb-3">
            <span class="field-label">Default language</span>
            <select v-model="language">
              <option
                v-for="lang in languages"
                :key="lang.code"
                :value="lang.code"
              >
                {{ lang.name }}
              </option>
            </select>
          </label>

          <div class="field mb-3" v-if="page_type == 1">
            <span class="field-label">Categories</span>
            <v-select
              v-model="category_id"
              :options="categories"
              label="name"
              :filter="
                (s, q) => s.filter((e) => e.name.toLowerCase().indexOf(q) > -1)
              "
              multiple
              placeholder="Select category"
              :reduce="(c) => c.id"
            />
          </div>

          <label class="field">
            <span class="field-label">Webstore</span>
            <select v-model="webshop">
              <option :value="null">All webstore</option>
              <option
                v-for="webstore in webshops"
                :key="webstore.id"
                :value="webstore.id"
              >
                {{ webstore.name }}
              </option>
            </select>
          </label>
        </div>
        <div class="modal-footer custom-input">
          <button
            class="btn btn-outline-primary"
            v-on:click="$emit('update:show', false)"
          >
            Cancel
          </button>
          <button
            v-if="page?.id"
            class="btn btn-primary"
            :disabled="slug.length < 1 && name.length < 1"
            v-on:click="editPage"
          >
            Save changes
          </button>
          <button
            v-else
            class="btn btn-primary"
            :disabled="slug.length < 1 && name.length < 1"
            v-on:click="addPage"
          >
            Add new page
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "../../modules/http";

const bootstrap = require("bootstrap");

// Search results cut the title around 60 characters and the description
// around 160, so anything longer is written but never read. A description
// under 70 is not an error — it is just short enough to be worth a look.
const TITLE_MAX = 60;
const DESCRIPTION_MIN = 70;
const DESCRIPTION_MAX = 160;

export default {
  name: "AddPage",
  props: {
    show: Boolean,
    page: Object,
    webshops: Array,
    categories: Array,
    page_type: Number,
    // Free-text page category (stored client-side by the page lists)
    pageCategory: {
      type: String,
      default: "",
    },
    categorySuggestions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:show", "add", "edit"],
  data() {
    return {
      myModal: undefined,
      title: {},
      description: {},
      name: "",
      slug: "",
      selectedLanguage: "",
      language: "",
      languages: [],
      category_id: [],
      webshop: "",
      categoryValue: "",
    };
  },
  computed: {
    completedLanguages() {
      return this.languages.filter((l) => this.langStatus(l.code) === "done")
        .length;
    },
    missingLanguageNames() {
      const missing = this.languages
        .filter((l) => this.langStatus(l.code) !== "done")
        .map((l) => l.name);
      return missing.length > 0 ? missing.join(", ") : "";
    },
    titleState() {
      return this.fieldState(this.valueFor("title"), {
        max: TITLE_MAX,
        label: "title",
      });
    },
    descriptionState() {
      return this.fieldState(this.valueFor("description"), {
        min: DESCRIPTION_MIN,
        max: DESCRIPTION_MAX,
        label: "description",
      });
    },
    selectedIssues() {
      return this.issuesFor(this.selectedLanguage?.code);
    },
    // Shown (read-only) in front of the slug so it's obvious the field is
    // the page's web address, not some technical "slug"
    slugDomain() {
      const list = this.webshops ?? [];
      const shop = this.webshop
        ? list.find((w) => w.id === this.webshop)
        : list.find((w) => w.is_default) ?? list[0];
      return shop?.domain ?? "test.com";
    },
  },
  mounted() {
    this.myModal = new bootstrap.Modal(this.$refs.modal, {
      backdrop: "static",
    });
    if (this.show) {
      this.myModal.show();
      this.loadLanguages();
    }
    // Nothing in an else branch on purpose: a modal that has just been
    // constructed is already hidden, and calling hide() on it throws inside
    // bootstrap and leaves a stuck backdrop behind — a page that looks normal
    // and ignores every click.
  },
  unmounted() {
    if (this.myModal) {
      this.myModal.dispose();
      this.myModal = undefined;
    }
    document
      .querySelectorAll(".modal-backdrop, .offcanvas-backdrop")
      .forEach((el) => el.remove());
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("padding-right");
  },
  watch: {
    page: {
      deep: true,
      immediate: true,
      handler() {
        if (this.page) {
          this.title = this.page.title ?? {};
          this.name = this.page.name ?? "";
          this.description = this.page.description ?? {};
          this.slug = this.page.slug ?? "";
          this.language = this.page.language_code ?? "";
          this.webshop = this.page.webshop_id;
          this.category_id = this.page.category_id ?? [];
        } else {
          this.title = {};
          this.description = {};
          this.name = "";
          this.slug = "";
          this.language = "";
          this.webshop = "";
          this.category_id = [];
        }
      },
    },
    show() {
      if (this.show) {
        this.myModal.show();
        this.loadLanguages();
      } else {
        this.myModal.hide();
      }
    },
    name() {
      // Only auto-generate the slug for new pages — renaming an existing
      // page must not silently change its live URL.
      if (this.page?.id) {
        return;
      }
      this.slug = this.name
        .replace(/[\W_]/g, "-")
        .toLowerCase()
        .replace(/--/g, "-");
    },
    pageCategory: {
      immediate: true,
      handler() {
        this.categoryValue = this.pageCategory ?? "";
      },
    },
  },
  methods: {
    loadLanguages() {
      if (this.languages.length > 0) {
        return;
      }
      http.fetch("/languages").then((data) => {
        this.languages = data;
        this.selectedLanguage = data[0];
        // The API requires a valid language_code, but this field started
        // empty and nothing forced a choice — so saving a new page failed
        // silently. Preselect one, and never by list order: the API returns
        // languages alphabetically, which would quietly pick Czech.
        if (!this.language) {
          this.language =
            data.find((lang) => lang.code === "en")?.code ?? data[0]?.code;
        }
      });
    },
    valueFor(field) {
      const code = this.selectedLanguage?.code;
      return code ? this[field]?.[code] ?? "" : "";
    },
    // One field's state, used both for the live counter next to the input and
    // for the language dots. Length matters as much as presence here: a
    // 294-character description is filled in and still unusable.
    // `blocking` separates "this will not work" from "this could be better":
    // an empty field and one Google will cut off are real problems, a short
    // description is only a suggestion and must not turn the dot yellow —
    // a warning that cries wolf gets ignored within a week.
    fieldState(raw, { min = 0, max, label }) {
      const length = (raw ?? "").trim().length;
      if (length === 0) {
        return {
          tone: "empty",
          text: "empty",
          issue: `${label} is empty`,
          blocking: true,
        };
      }
      if (length > max) {
        return {
          tone: "over",
          text: `${length}/${max} — too long`,
          issue: `${label} is ${length} characters, ${max} is the limit`,
          blocking: true,
        };
      }
      if (length < min) {
        return {
          tone: "short",
          text: `${length}/${max} — short`,
          issue: `${label} is only ${length} characters`,
          blocking: false,
        };
      }
      return {
        tone: "ok",
        text: `${length}/${max}`,
        issue: null,
        blocking: false,
      };
    },
    statesFor(code) {
      return [
        this.fieldState(this.title?.[code] ?? "", {
          max: TITLE_MAX,
          label: "Title",
        }),
        this.fieldState(this.description?.[code] ?? "", {
          min: DESCRIPTION_MIN,
          max: DESCRIPTION_MAX,
          label: "Description",
        }),
      ];
    },
    issuesFor(code) {
      if (!code) {
        return [];
      }
      return this.statesFor(code)
        .map((state) => state.issue)
        .filter((issue) => issue !== null);
    },
    // "done" now means usable, not merely non-empty — otherwise the green dot
    // told the team a field was finished while it was still too long to show.
    langStatus(code) {
      const states = this.statesFor(code);
      const empty = states.filter((state) => state.tone === "empty").length;
      if (empty === states.length) {
        return "empty";
      }
      if (empty > 0) {
        return "partial";
      }
      return states.some((state) => state.blocking) ? "warn" : "done";
    },
    langStatusTitle(lang) {
      const status = this.langStatus(lang.code);
      if (status === "empty") {
        return `${lang.name}: not filled yet`;
      }
      const issues = this.issuesFor(lang.code);
      if (issues.length === 0) {
        return `${lang.name}: title and description are filled`;
      }
      return `${lang.name}: ${issues.join(", ")}`;
    },
    // Show the default language's text as placeholder so it's clear what
    // needs to be translated.
    fallbackFor(field) {
      const source = this[field] ?? {};
      const base = source[this.language] ?? Object.values(source)[0] ?? "";
      return base.length > 0 ? base : "";
    },
    editPage() {
      this.$emit("edit", {
        id: this.page.id,
        title: this.title,
        description: this.description,
        name: this.name,
        slug: this.slug,
        language_code: this.language,
        webshop_id: this.webshop ?? "",
        category_id: this.category_id,
        category: this.categoryValue.trim(),
      });
    },
    // The dialog used to close itself before the request was even sent, so a
    // rejected save looked exactly like a successful one — the page simply
    // never appeared. It stays open now; the list closes it once the server
    // has actually accepted the page.
    addPage() {
      this.$emit("add", {
        title: this.title,
        description: this.description,
        name: this.name,
        slug: this.slug,
        language_code: this.language,
        webshop_id: this.webshop ?? "",
        category_id: this.category_id,
        category: this.categoryValue.trim(),
      });
    },
  },
};
</script>
<style scoped>
.head-stack {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.head-label {
  font-size: 11px;
  font-weight: 700;
  color: #768692;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
/* The page name doubles as the rename field: ~20% larger than the old
   16px modal title, styled as an inline-editable heading */
.name-input {
  border: none;
  background: transparent;
  font-size: 19px;
  font-weight: 700;
  color: #121212;
  width: 100%;
  padding: 0;
  outline: none;
  border-bottom: 1.5px dashed transparent;
  border-radius: 0;
}
.name-input:hover {
  border-bottom-color: #c4b5fd;
}
.name-input:focus {
  border-bottom-color: #7c3aed;
}
/* Address field: fixed domain prefix + editable slug in one control */
.slug-input {
  display: flex;
  align-items: stretch;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}
.slug-input:focus-within {
  border-color: #7c3aed;
}
.slug-domain {
  display: flex;
  align-items: center;
  padding: 7px 2px 7px 9px;
  font-size: 13px;
  font-weight: 600;
  color: #768692;
  background: #f1f5f9;
  border-right: 1px solid #e2e8f0;
  white-space: nowrap;
  user-select: none;
}
.slug-input input {
  border: none !important;
  border-radius: 0 !important;
  flex: 1;
  min-width: 0;
  outline: none;
}
.lang-section {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 14px;
}
.lang-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.lang-pills {
  margin-bottom: 12px;
  flex-wrap: wrap;
  width: 100%;
}
.lang-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: none;
  display: inline-block;
}
.lang-done {
  background: #149200;
}
.lang-partial {
  background: #8d7803;
}
.lang-warn {
  background: #8d7803;
}
.lang-empty {
  background: #c0bdbd;
}
/* Label and counter share one line so the count sits with the field it
   belongs to rather than floating under the input. */
.field-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.count-hint {
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.count-ok {
  color: #149200;
}
.count-short,
.count-over {
  color: #8d7803;
}
.count-empty {
  color: #b42318;
}
/* An empty SEO field is the thing nobody noticed, so the field itself says
   so instead of relying on the counter alone. */
.field input.input-empty {
  border-color: #f0b3ae;
  background: #fff8f7;
}
.issue-line {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  font-weight: 600;
  color: #8d7803;
}
</style>
